import { getNode, applyPropertyState } from '../nodeUtils.js';

function eqValue(a, b) {
    if (a === b) return true;
    // For objects/arrays (e.g. hass_config) use JSON comparison
    if (typeof a === 'object' && a !== null) return JSON.stringify(a) === JSON.stringify(b);
    return false;
}

// Flatten a capturePropertyState()-shaped state into a single-level map for
// diffing: top-level properties keep their bare name, default-state style
// properties are keyed "part:prop" (e.g. "indicator:bg_color") for backward-
// compatible history-detail display, and non-default state overrides are
// keyed "part:state:prop" (e.g. "main:checked:bg_color") — all internal
// history-only labels, never firmware wire keys. x/y/w/h/hass_config pass
// through as-is.
function flattenState(state) {
    const flat = {};
    if (!state) return flat;
    for (const [key, value] of Object.entries(state)) {
        if (key === 'properties' || key === 'styles') continue;
        flat[key] = value;
    }
    for (const [key, value] of Object.entries(state.properties ?? {})) {
        flat[key] = value;
    }
    for (const [partName, partData] of Object.entries(state.styles ?? {})) {
        for (const [stateName, stateData] of Object.entries(partData?.states ?? {})) {
            for (const [prop, value] of Object.entries(stateData?.properties ?? {})) {
                const key = stateName === 'default' ? `${partName}:${prop}` : `${partName}:${stateName}:${prop}`;
                flat[key] = value;
            }
        }
    }
    return flat;
}

// The prop half of a diff key ("indicator:bg_color" -> "bg_color",
// "main:checked:bg_color" -> "bg_color", "text" -> "text") — always the
// segment after the LAST colon, so it works for both 2- and 3-tier keys.
function keyProp(key) {
    const idx = key.lastIndexOf(':');
    return idx === -1 ? key : key.slice(idx + 1);
}

function hassChangeDescription(prev, next) {
    const prevProps  = JSON.stringify(prev?.properties  ?? []);
    const nextProps  = JSON.stringify(next?.properties  ?? []);
    const prevEvts   = JSON.stringify(prev?.events       ?? []);
    const nextEvts   = JSON.stringify(next?.events       ?? []);
    const prevAuto   = JSON.stringify(prev?.automation   ?? []);
    const nextAuto   = JSON.stringify(next?.automation   ?? []);
    const propsChanged  = prevProps !== nextProps;
    const eventsChanged = prevEvts  !== nextEvts;
    const autoChanged   = prevAuto  !== nextAuto;
    if (propsChanged  && (eventsChanged || autoChanged)) return 'HA Config Changed';
    if (propsChanged)   return 'Entity Binding Changed';
    if (autoChanged)    return 'Automation Changed';
    if (eventsChanged)  return 'Event Changed';
    return 'HA Config Changed';
}

function gpioChangeDescription(prev, next) {
    const prevEvents = JSON.stringify(prev?.events ?? []);
    const nextEvents = JSON.stringify(next?.events ?? []);
    const prevState = JSON.stringify(prev?.state ?? []);
    const nextState = JSON.stringify(next?.state ?? []);
    if (prevEvents !== nextEvents && prevState !== nextState) return 'GPIO Bindings Changed';
    if (prevEvents !== nextEvents) return 'GPIO Event Action Changed';
    if (prevState !== nextState) return 'GPIO State Binding Changed';
    return 'GPIO Bindings Changed';
}

function descriptionFromChanges(changed) {
    if (changed.length === 0) return 'Edit property';
    const hassKey = changed.find(c => c.key === 'hass_config');
    if (hassKey) return hassChangeDescription(hassKey.prev, hassKey.next);
    const gpioKey = changed.find(c => c.key === 'gpio_bindings');
    if (gpioKey) return gpioChangeDescription(gpioKey.prev, gpioKey.next);
    const colorKey = changed.find(c => c.key.includes('color'));
    if (colorKey) return 'Change Color';
    const textKey = changed.find(c => keyProp(c.key) === 'text' || keyProp(c.key) === 'text_text');
    if (textKey) return 'Change Text';
    if (changed.length === 1) return 'Change ' + changed[0].key.replace(/[:_]/g, ' ');
    return 'Edit properties';
}

function categoryFromChanges(changed) {
    if (changed.some(c => c.key === 'hass_config')) return 'homeassistant';
    if (changed.some(c => c.key === 'gpio_bindings')) return 'gpio';
    if (changed.some(c => c.key.includes('color') || ['text', 'text_text', 'radius'].includes(keyProp(c.key)))) return 'styling';
    return 'styling';
}

export default class PropertyChangeCommand {
    constructor({ description, objectUuid, prevState, nextState, pageUuid }) {
        this.id = crypto.randomUUID();
        this.timestamp = Date.now();
        this.objectUuid = objectUuid;
        this.affectedUuids = [objectUuid];
        this.pageUuid = pageUuid ?? null;
        this.prevState = prevState;
        this.nextState = nextState;

        // Diff states to produce a structured change log for the details panel.
        // Flatten first so top-level properties and part-scoped styles compare
        // uniformly ("indicator:bg_color" style keys are history-internal labels).
        this.changedProperties = [];
        const flatPrev = flattenState(prevState);
        const flatNext = flattenState(nextState);
        const allKeys = new Set([...Object.keys(flatPrev), ...Object.keys(flatNext)]);
        for (const key of allKeys) {
            if (!eqValue(flatPrev[key] ?? null, flatNext[key] ?? null)) {
                this.changedProperties.push({ key, prev: flatPrev[key], next: flatNext[key] });
            }
        }

        this.description = description ?? descriptionFromChanges(this.changedProperties);
        this.category    = categoryFromChanges(this.changedProperties);

    }

    execute() {
        const node = getNode(this.objectUuid);
        if (node) applyPropertyState(node, this.nextState);
    }

    undo() {
        const node = getNode(this.objectUuid);
        if (node) applyPropertyState(node, this.prevState);
    }

    toJSON() {
        return {
            type: 'PropertyChange',
            id: this.id,
            timestamp: this.timestamp,
            payload: {
                objectUuid: this.objectUuid,
                pageUuid: this.pageUuid,
                prevState: this.prevState,
                nextState: this.nextState,
            },
        };
    }
}
